import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Upload, 
  Search, 
  FileText, 
  Download, 
  Trash2, 
  File,
  Calendar,
  HardDrive,
  AlertCircle,
  X,
  UploadCloud,
  FileCheck,
  Users,
  Brain,
  UserPlus,
  ChevronRight,
  ChevronDown,
  Globe,
  UserCheck,
  Filter,
  Share2,
  GitBranch,
  Briefcase,
  Tag,
  Loader2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from 'react-query';

import { candidatesAPI, cvsAPI, handleApiError, downloadFile } from '../../utils/api';
import { getErrorMessage } from '../../utils/errorTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import CandidateModal from '../../components/CandidateModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import useConfirm from '../../hooks/useConfirm';
import CVPreview from '../../components/CVPreview';
import SendAppointmentModal from '../../components/appointments/SendAppointmentModal';
import AppointmentStatusIndicator from '../../components/appointments/AppointmentStatusIndicator';
import Pagination from '../../components/Pagination';
import eventEmitter, { EVENTS } from '../../utils/eventEmitter';

// Helper functions outside component to avoid circular references
const groupCvsByOfferHelper = (cvs) => {
  const grouped = {};
  const ungrouped = [];

  cvs.forEach(cv => {
    if (cv.offer_id && cv.offer_title) {
      if (!grouped[cv.offer_id]) {
        grouped[cv.offer_id] = {
          id: cv.offer_id,
          title: cv.offer_title,
          cvs: []
        };
      }
      grouped[cv.offer_id].cvs.push(cv);
    } else {
      ungrouped.push(cv);
    }
  });

  return { grouped: Object.values(grouped), ungrouped };
};

const findCvPageHelper = (cvId, cvList, itemsPerPage) => {
  const index = cvList.findIndex(cv => cv.id === cvId);
  if (index === -1) return 1;
  return Math.floor(index / itemsPerPage) + 1;
};

// Helper function for file type detection
const getFileTypeFromMime = (mimeType) => {
  if (!mimeType) return 'Unknown';
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'DOCX';
  return 'Unknown';
};

const isSupportedCvFile = (file) => {
  if (!file) return false;
  const name = file.name?.toLowerCase() || '';
  const mime = file.type;
  return (
    mime === 'application/pdf' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    name.endsWith('.pdf') ||
    name.endsWith('.docx')
  );
};

const CVsPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { confirm, confirmProps } = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('offers'); // 'offers' | 'manual'
  const [originFilter, setOriginFilter] = useState('application');
  const [offerFilter, setOfferFilter] = useState('all');
  const [expandedOffers, setExpandedOffers] = useState(new Set());
  const [dragActive, setDragActive] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // Pagination states
  const [offerPages, setOfferPages] = useState({}); // { offerId: currentPage }
  const [manualCvsPage, setManualCvsPage] = useState(1);
  const [ungroupedCvsPage, setUngroupedCvsPage] = useState(1);
  const itemsPerPage = 15;
  const [analyzingCv, setAnalyzingCv] = useState(null);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [highlightedCvId, setHighlightedCvId] = useState(null);
  const [showCVPreview, setShowCVPreview] = useState(false);
  const [cvPreviewData, setCvPreviewData] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedCandidateForAppointment, setSelectedCandidateForAppointment] = useState(null);

  // Reset pagination when filters change
  useEffect(() => {
    setOfferPages({});
    setManualCvsPage(1);
    setUngroupedCvsPage(1);
  }, [searchTerm, fileTypeFilter, originFilter, offerFilter, activeTab]);

  const cvQueryParams = useMemo(
    () => ({
      search: searchTerm || undefined,
      file_type: fileTypeFilter !== 'all' ? fileTypeFilter : undefined,
      origin: originFilter,
      offer_id: offerFilter !== 'all' ? offerFilter : undefined
    }),
    [searchTerm, fileTypeFilter, originFilter, offerFilter]
  );

  useEffect(() => {
    const targetOrigin = activeTab === 'offers' ? 'application' : 'manual';
    if (originFilter !== targetOrigin) {
      setOriginFilter(targetOrigin);
    }
    if (activeTab !== 'offers' && offerFilter !== 'all') {
      setOfferFilter('all');
    }
  }, [activeTab, originFilter, offerFilter]);

  const cvsQuery = useQuery(
    ['cvs', cvQueryParams],
    async () => {
      const response = await cvsAPI.getAll(cvQueryParams);
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        toast.error(t('cvs.errors.loadFailed', 'Erreur lors du chargement des CVs'));
      }
    }
  );

  const statsQuery = useQuery(
    ['cvsStats', user?.id],
    async () => {
      const response = await cvsAPI.getStats();
      return response.data;
    },
    {
      onError: () => {
        // Silent stats failure
      }
    }
  );

  const candidatesQuery = useQuery(
    ['candidatesAll'],
    async () => {
      const response = await candidatesAPI.getAll({ include_all: true });
      const data = response.data?.candidates || response.data || [];
      return Array.isArray(data) ? data : [];
    },
    {
      onError: () => {
        // Silent failure
      }
    }
  );

  const publishedOffersQuery = useQuery(
    ['cvsPublishedOffers'],
    async () => {
      const response = await cvsAPI.getPublishedOffers();
      return response.data;
    },
    {
      enabled: activeTab === 'offers',
      onError: () => {
        // Silent failure
      }
    }
  );

  const cvs = useMemo(() => cvsQuery.data?.cvs || [], [cvsQuery.data]);
  const loading = cvsQuery.isLoading;
  const loadError = cvsQuery.isError;
  const statsRaw = useMemo(
    () => statsQuery.data || { total: 0, pdf: 0, docx: 0, manual: 0, application: 0 },
    [statsQuery.data]
  );
  const stats = useMemo(() => ({
    total: Number(statsRaw.total) || 0,
    pdf: Number(statsRaw.pdf) || 0,
    docx: Number(statsRaw.docx) || 0,
    manual: Number(statsRaw.manual) || 0,
    application: Number(statsRaw.application) || 0
  }), [statsRaw]);
  const candidates = candidatesQuery.data || [];
  const publishedOffers = useMemo(() => publishedOffersQuery.data?.offers || [], [publishedOffersQuery.data]);

  const activeFilters = useMemo(() => {
    const filters = [];
    if (searchTerm.trim()) {
      filters.push({
        key: 'search',
        label: `${t('cvs.filters.search', 'Recherche')}: ${searchTerm.trim()}`,
        onClear: () => setSearchTerm('')
      });
    }
    if (fileTypeFilter !== 'all') {
      filters.push({
        key: 'fileType',
        label: `${t('cvs.filters.type', 'Type')}: ${fileTypeFilter.toUpperCase()}`,
        onClear: () => setFileTypeFilter('all')
      });
    }
    if (offerFilter !== 'all') {
      const offerTitle = publishedOffers.find((offer) => String(offer.id) === String(offerFilter))?.title;
      filters.push({
        key: 'offer',
        label: `${t('cvs.filters.offer', 'Offre')}: ${offerTitle || offerFilter}`,
        onClear: () => setOfferFilter('all')
      });
    }
    return filters;
  }, [searchTerm, fileTypeFilter, offerFilter, publishedOffers, t]);

  // Fonctions de gestion des groupes d'offres
  const toggleOfferExpansion = (offerId) => {
    setExpandedOffers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(offerId)) {
        newSet.delete(offerId);
      } else {
        newSet.add(offerId);
      }
      return newSet;
    });
  };

  const expandAllOffers = () => {
    const allOfferIds = [...new Set(filteredCvs.filter(cv => cv.offer_id).map(cv => cv.offer_id))];
    setExpandedOffers(new Set(allOfferIds));
  };

  const collapseAllOffers = () => {
    setExpandedOffers(new Set());
  };

  // Use the helper function defined outside the component

  // Share candidate to kanban
  const handleShareToKanban = async (candidateId) => {
    try {
      await candidatesAPI.shareToKanban(candidateId);
      toast.success(t('cvs.messages.sharedToKanban', 'Candidat partagé au kanban avec succès'));
      
      // Émettre l'événement de partage au kanban
      eventEmitter.emit(EVENTS.CANDIDATE_SHARED_TO_KANBAN, { candidateId });
      eventEmitter.emit(EVENTS.KANBAN_REFRESH_NEEDED);
      
      queryClient.invalidateQueries('candidatesAll'); // Rafraîchir la liste
    } catch (error) {
      const errorMessage = handleApiError(error, t('cvs.errors.shareFailed', 'Erreur lors du partage du candidat'));
      toast.error(errorMessage);
    }
  };

  // Unshare candidate from kanban
  const handleUnshareFromKanban = async (candidateId) => {
    try {
      await candidatesAPI.unshareFromKanban(candidateId);
      toast.success(t('cvs.messages.unsharedFromKanban', 'Candidat retiré du kanban avec succès'));
      
      // Émettre l'événement de retrait du kanban
      eventEmitter.emit(EVENTS.CANDIDATE_UNSHARED_FROM_KANBAN, { candidateId });
      eventEmitter.emit(EVENTS.KANBAN_REFRESH_NEEDED);
      
      queryClient.invalidateQueries('candidatesAll'); // Rafraîchir la liste
    } catch (error) {
      const errorMessage = handleApiError(error, t('cvs.errors.unshareFailed', 'Erreur lors du retrait du candidat'));
      toast.error(errorMessage);
    }
  };

  // Filter CVs based on search, file type, origin, and offer
  const filteredCvs = React.useMemo(() => {
    return cvs.filter(cv => {
      const matchesSearch = !searchTerm || 
        cv.original_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = fileTypeFilter === 'all' || 
        getFileTypeFromMime(cv.mime_type).toLowerCase() === fileTypeFilter.toLowerCase();
      const matchesOrigin = (cv.origin || 'manual') === originFilter;
      const matchesOffer = offerFilter === 'all' || 
        (cv.offer_id && cv.offer_id.toString() === offerFilter);
      return matchesSearch && matchesType && matchesOrigin && matchesOffer;
    });
  }, [cvs, searchTerm, fileTypeFilter, originFilter, offerFilter]);

  const hasActiveUserFilters = useMemo(
    () => Boolean(searchTerm || fileTypeFilter !== 'all' || (activeTab === 'offers' && offerFilter !== 'all')),
    [searchTerm, fileTypeFilter, activeTab, offerFilter]
  );

  useEffect(() => {
    const highlightParam = searchParams.get('highlight');
    if (!highlightParam) return;

    const cvId = parseInt(highlightParam, 10);
    if (!Number.isFinite(cvId)) return;

    let cancelled = false;

    const resolveTabFromHighlightedCv = async () => {
      try {
        const response = await cvsAPI.getById(cvId);
        const cv = response.data?.cv || response.data;
        const targetTab = cv?.origin === 'application' ? 'offers' : 'manual';
        if (!cancelled && targetTab && targetTab !== activeTab) {
          setActiveTab(targetTab);
        }
      } catch (error) {
        // Ignore: highlight will be cleared by existing flow if needed
      }
    };

    resolveTabFromHighlightedCv();
    return () => {
      cancelled = true;
    };
  }, [searchParams, activeTab]);

  // Handle URL parameter for highlighting specific CV
  useEffect(() => {
    const highlightParam = searchParams.get('highlight');
    if (highlightParam && filteredCvs.length > 0) {
      const cvId = parseInt(highlightParam, 10);
      setHighlightedCvId(cvId);
      
      // Find the CV in the filtered list
      const targetCv = filteredCvs.find(cv => cv.id === cvId);
      if (!targetCv) return;
      
      // Determine tab/page destination for highlighted CV
      if (targetCv.origin === 'application') {
        setActiveTab('offers');
        if (targetCv.offer_id) {
          const applicationCVs = filteredCvs.filter(cv => cv.origin === 'application');
          const { grouped } = groupCvsByOfferHelper(applicationCVs);
          const offerGroup = grouped.find(group => group.id === targetCv.offer_id);

          if (offerGroup) {
            setExpandedOffers(prev => new Set([...prev, targetCv.offer_id]));
            const page = findCvPageHelper(cvId, offerGroup.cvs, itemsPerPage);
            setOfferPages(prev => ({ ...prev, [targetCv.offer_id]: page }));
          }
        } else {
          const applicationUngrouped = filteredCvs.filter(
            (cv) => cv.origin === 'application' && (!cv.offer_id || !cv.offer_title)
          );
          const page = findCvPageHelper(cvId, applicationUngrouped, itemsPerPage);
          setUngroupedCvsPage(page);
        }
      } else {
        setActiveTab('manual');
        const manualCVs = filteredCvs.filter((cv) => cv.origin !== 'application');
        const page = findCvPageHelper(cvId, manualCVs, itemsPerPage);
        setManualCvsPage(page);
      }
      
      // Scroll to the highlighted CV with improved centering
      setTimeout(() => {
        const cvElement = document.getElementById(`cv-card-${cvId}`);
        if (cvElement) {
          // First scroll to center the element perfectly
          cvElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
          
          // Add a small additional scroll adjustment for better visual centering
          setTimeout(() => {
            const rect = cvElement.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const currentScroll = window.pageYOffset;
            const elementCenter = rect.top + currentScroll + (rect.height / 2);
            const windowCenter = windowHeight / 2;
            const scrollTo = elementCenter - windowCenter;
            
            window.scrollTo({
              top: scrollTo,
              behavior: 'smooth'
            });
          }, 500);
        }
      }, 1000);

      // Clear the highlight after 5 seconds
      setTimeout(() => {
        setHighlightedCvId(null);
        // Remove the highlight parameter from URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('highlight');
        setSearchParams(newSearchParams, { replace: true });
      }, 5000);
    }
  }, [searchParams, setSearchParams, filteredCvs, itemsPerPage]);

  // File upload handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles = droppedFiles.filter(isSupportedCvFile);
      
      const invalidFiles = droppedFiles.filter(file => 
        !isSupportedCvFile(file)
      );
      
      if (invalidFiles.length > 0) {
        toast.error(t('cvs.errors.invalidFileType'));
        return;
      }
      
      if (validFiles.length === 0) {
        toast.error(t('cvs.errors.noValidFiles'));
        return;
      }
      
      setSelectedFiles(validFiles);
      setShowUploadModal(true);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFilesList = Array.from(e.target.files);
      const validFiles = selectedFilesList.filter(isSupportedCvFile);
      
      const invalidFiles = selectedFilesList.filter(file => 
        !isSupportedCvFile(file)
      );
      
      if (invalidFiles.length > 0) {
        toast.error(t('cvs.errors.invalidFileType'));
        e.target.value = '';
        return;
      }
      
      if (validFiles.length === 0) {
        toast.error(t('cvs.errors.noValidSelected'));
        e.target.value = '';
        return;
      }
      
      setSelectedFiles(validFiles);
      setShowUploadModal(true);
      // Reset the input so the same files can be selected again if needed
      e.target.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;

    setUploading(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      // Toujours extraire le texte pour permettre l'analyse IA
      formData.append('extractText', 'true');

      const response = await cvsAPI.upload(formData);
      const { cvs: uploadedCvs } = response.data;
      
      // Séparer les CVs uploadés avec succès des doublons
      const successfulUploads = uploadedCvs.filter(cv => !cv.duplicate);
      const duplicates = uploadedCvs.filter(cv => cv.duplicate);
      
      // Afficher les messages appropriés
      if (successfulUploads.length > 0) {
        toast.success(
          t('cvs.messages.uploadSuccess', '{{count}} CV(s) uploadé(s) avec succès')
            .replace('{{count}}', successfulUploads.length)
        );
      }
      
      if (duplicates.length > 0) {
        duplicates.forEach(duplicate => {
          toast.warning(
            t('cvs.messages.duplicateFile', 'Document "{{filename}}" existe déjà. Si c\'est une mise à jour, veuillez supprimer l\'ancien document ou changer son nom.')
              .replace('{{filename}}', duplicate.original_name),
            { autoClose: 8000 }
          );
        });
      }
      
      // Si aucun fichier n'a été uploadé avec succès
      if (successfulUploads.length === 0 && duplicates.length > 0) {
        toast.info(t('cvs.messages.noNewFiles', 'Aucun nouveau CV n\'a été ajouté - tous les fichiers existaient déjà.'));
      }
      
      setSelectedFiles([]);
      setShowUploadModal(false);
      queryClient.invalidateQueries('cvs');
      queryClient.invalidateQueries(['cvsStats', user?.id]);
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      let fallbackMessage = t('cvs.errors.uploadFailed', 'Erreur lors de l\'upload');
      if (errorCode === 'CV_INVALID_FILE_TYPE') {
        fallbackMessage = t('cvs.errors.invalidFileType');
      } else if (errorCode === 'CV_FILE_TOO_LARGE') {
        fallbackMessage = t('cvs.errors.fileTooLarge', 'Fichier trop volumineux');
      } else if (errorCode === 'CV_TOO_MANY_FILES') {
        fallbackMessage = t('cvs.errors.tooManyFiles', 'Trop de fichiers sélectionnés');
      }
      const errorMessage = getErrorMessage(error, t, fallbackMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // CV actions
  const handleDelete = async (cvId, filename) => {
    try {
      // Vérifier les dépendances avant d'afficher le modal
      const response = await cvsAPI.checkDependencies(cvId);
      const dependencies = response.data;
      
      // Construire le message avec les avertissements
      let message = t('cvs.deleteModal.message').replace('{{filename}}', filename);
      
      if (dependencies.hasKanbanCard) {
        message += '\n\n' + t('cvs.deleteModal.warnings.hasKanbanCard');
      }
      
      if (dependencies.hasActiveAppointments) {
        message += '\n\n' + t('cvs.deleteModal.warnings.hasAppointments');
      }
      
      const result = await confirm({
        title: t('cvs.deleteModal.title'),
        message,
        type: 'danger',
        confirmText: t('cvs.deleteModal.confirm'),
        cancelText: t('cvs.deleteModal.cancel')
      });

      if (!result) return;

      await cvsAPI.delete(cvId);
      toast.success(t('cvs.messages.deleteSuccess'));
      
      // Mise à jour optimiste - supprimer le CV de la liste immédiatement
      queryClient.setQueryData(['cvs', cvQueryParams], (old) => {
        if (!old) return old;
        return { ...old, cvs: (old.cvs || []).filter((cv) => cv.id !== cvId) };
      });
      
      // Émettre l'événement de suppression de CV
      eventEmitter.emit(EVENTS.CV_DELETED, { 
        cvId, 
        hasKanbanCard: dependencies.hasKanbanCard 
      });
      
      // Si le CV avait une fiche candidat, émettre aussi l'événement de suppression de candidat
      if (dependencies.hasKanbanCard) {
        eventEmitter.emit(EVENTS.CANDIDATE_DELETED, {
          cvId,
          candidateCardId: dependencies.candidateCard?.id
        });
        // Demander un rafraîchissement du kanban
        eventEmitter.emit(EVENTS.KANBAN_REFRESH_NEEDED);
      }
      
      // Rafraîchir seulement les stats sans recharger tous les CVs
      queryClient.invalidateQueries(['cvsStats', user?.id]);
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      let fallbackMessage = t('cvs.errors.deleteFailed');
      if (errorCode === 'CV_HAS_DEPENDENCIES') {
        fallbackMessage = t('cvs.errors.hasDependencies', 'Ce CV ne peut pas être supprimé pour le moment.');
      }
      const errorMessage = handleApiError(error, fallbackMessage);
      toast.error(errorMessage);
    }
  };

  const handleDownload = async (cv) => {
    try {
      const response = await cvsAPI.getFile(cv.id, 'attachment');
      downloadFile(response.data, cv.original_name || cv.file_name || 'cv');
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      let fallbackMessage = t('cvs.errors.downloadFailed');
      if (errorCode === 'CV_NOT_FOUND') {
        fallbackMessage = t('cvs.errors.notFound', 'CV introuvable');
      }
      const errorMessage = handleApiError(error, fallbackMessage);
      toast.error(errorMessage);
    }
  };

  const handlePreviewCV = (cv) => {
    setCvPreviewData(cv);
    setShowCVPreview(true);
  };


  // AI Resume handler
  const handleAIResume = async (cv) => {
    if (!cv.extracted_text) {
      toast.warning(t('cvs.warnings.extractTextFirst', 'Veuillez d\'abord extraire le texte du CV'));
      return;
    }

    setAnalyzingCv(cv.id);
    
    try {
      await candidatesAPI.aiAnalyze({
        cv_id: cv.id,
        column_id: null
      });
      toast.success(t('cvs.messages.candidateCreated', 'Fiche candidat créée avec succès'));
      
      // Émettre l'événement de création de candidat
      eventEmitter.emit(EVENTS.CANDIDATE_CREATED, { cvId: cv.id });
      
      // Rafraîchir immédiatement les données avec mise à jour optimiste
      
      // Mettre à jour immédiatement l'état local du CV
      queryClient.setQueryData(['cvs', cvQueryParams], (old) => {
        if (!old) return old;
        return {
          ...old,
          cvs: (old.cvs || []).map((c) =>
            c.id === cv.id ? { ...c, has_candidate_card: true } : c
          )
        };
      });
      
      // Rafraîchir seulement les candidats pour s'assurer de la cohérence
      // Sans recharger tous les CVs (éviter la perte de position)
      try {
        queryClient.invalidateQueries('candidatesAll');
      } catch (refreshError) {
        // L'affichage optimiste est déjà fait, pas besoin de toast d'erreur
      }
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      if (errorCode === 'AI_CONFIG_MISSING') {
        const confirmed = await confirm({
          title: t('aiConfig.modal.title'),
          message: t('aiConfig.modal.message'),
          type: 'info',
          confirmText: t('aiConfig.modal.goToSettings'),
          cancelText: t('aiConfig.modal.cancel')
        });
        if (confirmed) {
          navigate('/dashboard/parametres?tab=developer');
        }
        return;
      }

      if (errorCode === 'AI_INVALID_TOKEN') {
        const errorMessage = getErrorMessage(
          error,
          t,
          t('aiConfig.errors.invalidToken', 'Clé API IA invalide')
        );
        toast.error(errorMessage);
        return;
      }

      if (error?.response?.status >= 500) {
        // Global interceptor already shows a server error toast
        return;
      }

      const errorMessage = getErrorMessage(
        error,
        t,
        t('cvs.errors.analysisFailed', 'Erreur lors de l\'analyse')
      );
      toast.error(errorMessage);
    } finally {
      setAnalyzingCv(null);
    }
  };

  // Edit candidate
  const handleEditCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setShowCandidateModal(true);
  };


  // Helper functions
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType?.includes('word')) return '📝';
    return '📋';
  };

  // Already moved above

  // Trouver la fiche candidat associée à un CV
  const getCandidateForCV = (cvId) => {
    const candidate = candidates.find(candidate => candidate.cv_id === cvId);
    return candidate;
  };

  // CV Card Component avec liaison fiche candidat
  const CVCard = ({ cv }) => {
    const associatedCandidate = getCandidateForCV(cv.id);
    const isApplicationCV = cv.origin === 'application';
    const isHighlighted = highlightedCvId === cv.id;
    
    return (
      <motion.div
        id={`cv-card-${cv.id}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ 
          opacity: 1, 
          y: 0,
          scale: isHighlighted ? 1.02 : 1,
          boxShadow: isHighlighted ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}
        transition={{ duration: 0.3 }}
        className={`bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-all duration-200 ${
          isApplicationCV ? 'border-l-4 border-purple-500' : 'border-l-4 border-blue-500'
        } ${
          isHighlighted ? 'ring-4 ring-blue-400 ring-opacity-50 border-blue-500 bg-blue-50' : ''
        }`}
      >
        <div className="space-y-3">
          {/* Highlighted CV indicator */}
          {isHighlighted && (
            <div className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full mb-3 text-center font-medium">
              📍 {t('cvs.labels.selectedFromApplication', 'CV sélectionné depuis la candidature')}
            </div>
          )}
          
          {/* Header avec origine distinctive */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center mb-2">
                <span className="text-2xl mr-2">{getFileIcon(cv.mime_type)}</span>
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {cv.original_name}
                </h3>
                {isApplicationCV && (
                  <Globe className="w-4 h-4 ml-2 text-purple-600" title={t('cvs.labels.receivedViaApplication', 'CV reçu via candidature publique')} />
                )}
              </div>
              
              {/* Informations candidature si origine application */}
              {isApplicationCV && cv.application_info && (
                <div className="bg-purple-50 p-2 rounded-md mb-3">
                  <div className="flex items-center text-sm text-purple-800">
                    <UserCheck className="w-4 h-4 mr-1" />
                    <span className="font-medium">
                      {cv.application_info.first_name} {cv.application_info.last_name}
                    </span>
                    {cv.application_info.email && (
                      <span className="ml-2 text-purple-600">• {cv.application_info.email}</span>
                    )}
                  </div>
                  {cv.application_info.offer_title && (
                    <div className="text-xs text-purple-600 mt-1">
                      {t('cvs.labels.offer', 'Offre')}: {cv.application_info.offer_title}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                <div className="flex items-center">
                  <File className="w-4 h-4 mr-1" />
                  {getFileTypeFromMime(cv.mime_type)}
                </div>
                <div className="flex items-center">
                  <HardDrive className="w-4 h-4 mr-1" />
                  {formatFileSize(cv.file_size)}
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {new Date(cv.created_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap mb-3">
                {/* Badge origine */}
                {isApplicationCV ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <UserCheck className="w-3 h-3 mr-1" />
                    {t('cvs.labels.application', 'Candidature')}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <Upload className="w-3 h-3 mr-1" />
                    {t('cvs.labels.manual', 'Manuel')}
                  </span>
                )}
                
                
                {associatedCandidate && (
                  <>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                      <UserPlus className="w-3 h-3 mr-1" />
                      {t('cvs.labels.candidateCardCreated', 'Fiche créée')}
                    </span>
                    {associatedCandidate.is_shared_to_kanban && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        <GitBranch className="w-3 h-3 mr-1" />
                        {t('cvs.labels.sharedToKanban', 'Partagé au kanban')}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Liaison fiche candidat */}
              {associatedCandidate && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-3 mt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {associatedCandidate.photo_url ? (
                        <img
                          src={associatedCandidate.photo_url}
                          alt={`${associatedCandidate.first_name} ${associatedCandidate.last_name}`}
                          className="h-10 w-10 rounded-full object-cover mr-3"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-purple-200 flex items-center justify-center mr-3">
                          <Users className="h-5 w-5 text-purple-600" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="text-sm font-medium text-purple-800">
                          {associatedCandidate.first_name} {associatedCandidate.last_name}
                        </div>
                        <div className="text-xs text-purple-600">
                          {associatedCandidate.title || associatedCandidate.specialty}
                        </div>
                        {associatedCandidate.score && (
                          <div className="flex items-center mt-1">
                            <div className="flex-1 bg-purple-200 rounded-full h-1.5 mr-2">
                              <div 
                                className="bg-purple-600 h-1.5 rounded-full" 
                                style={{ width: `${associatedCandidate.score}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-purple-600">{associatedCandidate.score}/100</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {!associatedCandidate.is_shared_to_kanban ? (
                        <button
                          onClick={() => handleShareToKanban(associatedCandidate.id)}
                          className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50"
                          title="Partager au kanban"
                          aria-label={t('cvs.actions.addToKanban')}
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUnshareFromKanban(associatedCandidate.id)}
                          className="text-orange-600 hover:text-orange-800 p-1 rounded hover:bg-orange-50"
                          title="Retirer du kanban"
                          aria-label={t('cvs.actions.removeFromKanban')}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEditCandidate(associatedCandidate)}
                        className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-50"
                        title="Voir/Modifier la fiche"
                        aria-label={t('cvs.actions.editCandidate')}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center space-x-2">
              
              {!associatedCandidate && cv.extracted_text && (
                <button
                  onClick={() => handleAIResume(cv)}
                  disabled={analyzingCv === cv.id}
                  className="p-2 text-gray-400 hover:text-purple-600 rounded-md hover:bg-purple-50 transition-colors"
                  title={t('cvs.actions.aiResume', 'Résumé AI + création fiche candidat')}
                  aria-label={t('cvs.actions.aiResume')}
                >
                  {analyzingCv === cv.id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  ) : (
                    <Brain className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePreviewCV(cv)}
                className="p-2 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                title={t('cvs.actions.preview', 'Prévisualiser le CV')}
                aria-label={t('cvs.actions.preview')}
              >
                <FileText className="w-4 h-4" />
              </button>
              {(cv.candidate_email || associatedCandidate?.email) && (
                <div className="flex items-center space-x-1">
                  <AppointmentStatusIndicator 
                    candidateEmail={cv.candidate_email || associatedCandidate?.email}
                    showLabel={false}
                    size="small"
                  />
                  <button
                    onClick={() => {
                      setSelectedCandidateForAppointment({
                        name: cv.candidate_name || associatedCandidate?.name || 'Candidat',
                        email: cv.candidate_email || associatedCandidate?.email,
                        offerId: cv.offer_id
                      });
                      setShowAppointmentModal(true);
                    }}
                    className="p-2 text-gray-400 hover:text-primary-600 rounded-md hover:bg-primary-50 transition-colors"
                    title={t('cvs.actions.sendAppointment', 'Envoyer une invitation de rendez-vous')}
                    aria-label={t('cvs.actions.sendAppointment')}
                  >
                    <CalendarIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              <button
                onClick={() => handleDownload(cv)}
                className="p-2 text-gray-400 hover:text-green-600 rounded-md hover:bg-green-50 transition-colors"
                title={t('cvs.actions.download', 'Télécharger')}
                aria-label={t('cvs.actions.download')}
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(cv.id, cv.original_name)}
                className="p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                title={t('cvs.actions.delete', 'Supprimer')}
                aria-label={t('cvs.actions.delete')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Note: CandidateCard component supprimé - les fiches sont maintenant intégrées dans CVCard

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-900">{t('cvs.title', 'Gestion des CVs')}</h1>
          {user?.role === 'rh' && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-full">
              <Users className="w-3 h-3 mr-1" />
              {t('cvs.labels.team', 'Équipe')}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">
          {user?.role === 'admin'
            ? t('cvs.subtitle.admin', 'CVs partagés avec votre équipe RH')
            : t('cvs.subtitle.hr', 'CVs accessibles à toute votre équipe')
          }
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">{t('cvs.stats.total', 'Total')}</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total || 0}</p>
            </div>
            <FileText className="h-8 w-8 text-indigo-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">{t('cvs.stats.documents', 'PDF/DOCX')}</p>
              <p className="text-2xl font-semibold text-rose-600">{(stats.pdf || 0) + (stats.docx || 0)}</p>
            </div>
            <span className="text-2xl">📄</span>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">{t('cvs.stats.manual', 'Upload manuel')}</p>
              <p className="text-2xl font-semibold text-blue-600">{stats.manual || 0}</p>
            </div>
            <Upload className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">{t('cvs.stats.applications', 'Candidatures')}</p>
              <p className="text-2xl font-semibold text-purple-600">{stats.application || 0}</p>
            </div>
            <UserCheck className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Upload Zone */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{t('cvs.upload.title', 'Importer des CVs')}</h2>
            <p className="text-xs text-gray-500">{t('cvs.upload.subtitle', 'Ajoutez des CVs PDF ou DOCX dans votre CVthèque')}</p>
          </div>
          <label htmlFor="file-upload" className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium cursor-pointer">
            <Upload className="w-4 h-4" />
            {t('cvs.actions.upload', 'Uploader des CVs')}
            <input
              id="file-upload"
              name="file-upload"
              type="file"
              className="sr-only"
              multiple
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFileSelect}
            />
          </label>
        </div>
        <div
          className={`mt-4 border-2 border-dashed rounded-lg p-6 transition-colors ${
            dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <UploadCloud className={`mx-auto h-10 w-10 ${dragActive ? 'text-indigo-600' : 'text-gray-400'} mb-3`} />
            <p className="text-sm text-gray-600">
              {t('cvs.upload.orDragDrop', 'Glissez-déposez vos fichiers ici')}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {t('cvs.upload.pdfOnly')}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              <AlertCircle className="inline w-3 h-3 mr-1" />
              {t('cvs.upload.maxFilesLimit', 'Maximum 50 fichiers par upload')}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs & Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-4">
          <button
            type="button"
            onClick={() => setActiveTab('offers')}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
              activeTab === 'offers'
                ? 'border-purple-300 bg-purple-50 text-purple-700'
                : 'border-gray-200 text-gray-600 hover:border-purple-200 hover:text-purple-700'
            }`}
          >
            <Briefcase className="h-4 w-4" />
            {t('cvs.tabs.offers', 'Banque CVs publiés par offres')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('manual')}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
              activeTab === 'manual'
                ? 'border-blue-300 bg-blue-50 text-blue-700'
                : 'border-gray-200 text-gray-600 hover:border-blue-200 hover:text-blue-700'
            }`}
          >
            <Upload className="h-4 w-4" />
            {t('cvs.tabs.manual', 'Banque CVs uploadés manuellement')}
          </button>
        </div>

        <div className="mt-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-900">{t('cvs.filters.title', 'Filtres')}</h2>
          {activeTab === 'offers' && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={expandAllOffers}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-purple-200"
              >
                {t('cvs.viewMode.expandAll', 'Tout déplier')}
              </button>
              <button
                onClick={collapseAllOffers}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-purple-200"
              >
                {t('cvs.viewMode.collapseAll', 'Tout replier')}
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className={`relative ${activeTab === 'offers' ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder={t('cvs.search.placeholder', 'Rechercher par nom de fichier...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-9 py-2 text-sm"
              aria-label={t('cvs.search.placeholder')}
            />
          </div>

          <div className="relative">
            <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <select
              value={fileTypeFilter}
              onChange={(e) => setFileTypeFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-9 py-2 text-sm"
              aria-label={t('cvs.filters.allTypes')}
            >
              <option value="all">{t('cvs.filters.allTypes', 'Tous les types')}</option>
              <option value="pdf">PDF</option>
              <option value="docx">DOCX</option>
            </select>
          </div>

          {activeTab === 'offers' && (
            <div className="relative">
              <Globe className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <select
                value={offerFilter}
                onChange={(e) => setOfferFilter(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-9 py-2 text-sm"
                aria-label={t('cvs.filters.allOffers')}
              >
                <option value="all">{t('cvs.filters.allOffers', 'Toutes les offres')}</option>
                {publishedOffers.map((offer) => (
                  <option key={offer.id} value={offer.id}>{offer.title}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {(activeFilters.length > 0) && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-200 pt-4 text-xs">
            <span className="text-gray-500">{t('cvs.filters.active', 'Filtres actifs')}:</span>
            {activeFilters.map((filter) => (
              <button
                key={filter.key}
                type="button"
                onClick={filter.onClear}
                className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700 hover:border-indigo-200"
              >
                <span>{filter.label}</span>
                <X className="w-3 h-3 text-gray-400" />
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setFileTypeFilter('all');
                setOfferFilter('all');
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              {t('cvs.filters.clearAll', 'Tout réinitialiser')}
            </button>
          </div>
        )}
      </div>

      {loadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span>{t('cvs.errors.loadFailed', 'Erreur lors du chargement des CVs')}</span>
          </div>
        </div>
      )}

      {/* CVs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('common.loading', 'Chargement...')}</span>
          </div>
        </div>
      ) : filteredCvs.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('cvs.emptyState.title', 'Aucun CV trouvé')}</h3>
          <p className="text-sm text-gray-600 mb-4">
            {hasActiveUserFilters
              ? t('cvs.emptyState.filterHint', 'Essayez de modifier vos filtres de recherche.')
              : t('cvs.emptyState.uploadHint', 'Commencez par uploader vos premiers CVs.')
            }
          </p>
          {!hasActiveUserFilters && (
            <label htmlFor="empty-state-file-upload" className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium cursor-pointer">
              <Upload className="w-4 h-4 mr-2" />
              {t('cvs.actions.upload', 'Uploader des CVs')}
              <input
                id="empty-state-file-upload"
                name="empty-state-file-upload"
                type="file"
                className="sr-only"
                multiple
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileSelect}
              />
            </label>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {(() => {
            if (activeTab === 'offers') {
              const applicationCVs = filteredCvs.filter((cv) => cv.origin === 'application');
              const { grouped, ungrouped } = groupCvsByOfferHelper(applicationCVs);

              return (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                          <Briefcase className="w-5 h-5 mr-2 text-purple-600" />
                          {t('cvs.sections.byOffer', 'CVs par Offre Publiée')}
                        </h2>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {applicationCVs.length}
                        </span>
                      </div>
                      {hasActiveUserFilters && (
                        <span className="text-xs text-gray-500">
                          {t('cvs.sections.searchResults', 'Résultats de recherche')}
                        </span>
                      )}
                    </div>
                  </div>

                  {grouped.length === 0 && ungrouped.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                      <AlertCircle className="mx-auto h-10 w-10 text-gray-300 mb-3" />
                      <p className="text-sm text-gray-600">
                        {t('cvs.emptyState.filterHint', 'Essayez de modifier vos filtres de recherche.')}
                      </p>
                    </div>
                  ) : (
                    <>
                      {grouped.map((offerGroup) => (
                        <motion.div
                          key={offerGroup.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="border border-purple-200 rounded-lg overflow-hidden bg-white shadow-sm"
                        >
                          <div
                            className="flex items-center justify-between p-4 bg-purple-50 cursor-pointer hover:bg-purple-100 transition-colors"
                            onClick={() => toggleOfferExpansion(offerGroup.id)}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                {expandedOffers.has(offerGroup.id) ? (
                                  <ChevronDown className="w-5 h-5 text-purple-600" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-purple-600" />
                                )}
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{offerGroup.title}</h3>
                                <p className="text-sm text-gray-600">
                                  {offerGroup.cvs.length} candidature{offerGroup.cvs.length > 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                              <Tag className="w-3 h-3 mr-1" />
                              {offerGroup.cvs.length}
                            </span>
                          </div>

                          {expandedOffers.has(offerGroup.id) && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="p-4 border-t border-purple-100"
                            >
                              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {offerGroup.cvs
                                  .slice(
                                    ((offerPages[offerGroup.id] || 1) - 1) * itemsPerPage,
                                    (offerPages[offerGroup.id] || 1) * itemsPerPage
                                  )
                                  .map((cv) => (
                                    <CVCard key={cv.id} cv={cv} />
                                  ))}
                              </div>
                              <Pagination
                                currentPage={offerPages[offerGroup.id] || 1}
                                totalItems={offerGroup.cvs.length}
                                itemsPerPage={itemsPerPage}
                                onPageChange={(page) => setOfferPages((prev) => ({ ...prev, [offerGroup.id]: page }))}
                              />
                            </motion.div>
                          )}
                        </motion.div>
                      ))}

                      {ungrouped.length > 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <h2 className="text-lg font-semibold text-gray-900">
                                {t('cvs.sections.applicationsUnlinked', 'CVs de Candidatures (Sans Offre)')}
                              </h2>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                <AlertCircle className="w-4 h-4 mr-1" />
                                {ungrouped.length}
                              </span>
                            </div>
                          </div>
                          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {ungrouped
                              .slice((ungroupedCvsPage - 1) * itemsPerPage, ungroupedCvsPage * itemsPerPage)
                              .map((cv) => (
                                <CVCard key={cv.id} cv={cv} />
                              ))}
                          </div>
                          <Pagination
                            currentPage={ungroupedCvsPage}
                            totalItems={ungrouped.length}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setUngroupedCvsPage}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            }

            const manualCVs = filteredCvs.filter((cv) => cv.origin !== 'application');
            return (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {t('cvs.sections.manualFull', 'CVs Uploadés Manuellement')}
                    </h2>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Upload className="w-4 h-4 mr-1" />
                      {manualCVs.length}
                    </span>
                  </div>
                  {hasActiveUserFilters && (
                    <span className="text-xs text-gray-500">
                      {t('cvs.sections.searchResults', 'Résultats de recherche')}
                    </span>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {manualCVs
                    .slice((manualCvsPage - 1) * itemsPerPage, manualCvsPage * itemsPerPage)
                    .map((cv) => (
                      <CVCard key={cv.id} cv={cv} />
                    ))}
                </div>
                <Pagination
                  currentPage={manualCvsPage}
                  totalItems={manualCVs.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setManualCvsPage}
                />
              </div>
            );
          })()}
        </div>
      )}

      {/* Note: Les fiches candidats sont maintenant intégrées directement dans les cartes CV */}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('cvs.actions.upload', 'Uploader des CVs')}
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFiles([]);
                }}
                className="text-gray-400 hover:text-gray-600"
                aria-label={t('common.close')}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* File selection area */}
            {selectedFiles.length === 0 ? (
              <div className="mb-6">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="text-center">
                    <UploadCloud className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <div className="text-sm">
                      <label htmlFor="modal-file-upload" className="relative cursor-pointer rounded-md font-medium text-primary-600 hover:text-primary-500">
                        <span>{t('cvs.upload.clickToSelectFiles', 'Cliquez pour sélectionner vos fichiers')}</span>
                        <input
                          id="modal-file-upload"
                          name="modal-file-upload"
                          type="file"
                          className="sr-only"
                          multiple
                          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={handleFileSelect}
                        />
                      </label>
                      <span className="text-gray-500"> {t('cvs.upload.orDragDrop', 'ou glissez-déposez vos fichiers ici')}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {t('cvs.upload.pdfOnly')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      <AlertCircle className="inline w-3 h-3 mr-1" />
                      {t('cvs.upload.maxFilesLimit', 'Maximum 50 fichiers par upload')}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    {t('cvs.upload.selectedFiles', 'Fichiers sélectionnés')} ({selectedFiles.length})
                  </h4>
                  <button
                    onClick={() => setSelectedFiles([])}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    {t('cvs.upload.clearSelection', 'Tout supprimer')}
                  </button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center">
                        <FileCheck className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-sm font-medium">{file.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          {formatFileSize(file.size)}
                        </span>
                        <button
                          onClick={() => {
                            const newFiles = selectedFiles.filter((_, i) => i !== index);
                            setSelectedFiles(newFiles);
                          }}
                          className="text-red-500 hover:text-red-700"
                          aria-label={t('common.remove')}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <label htmlFor="modal-file-upload-add" className="cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter plus de fichiers
                    <input
                      id="modal-file-upload-add"
                      name="modal-file-upload-add"
                      type="file"
                      className="sr-only"
                      multiple
                      accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => {
                        if (e.target.files) {
                          const newFiles = Array.from(e.target.files);
                          const validFiles = newFiles.filter(isSupportedCvFile);
                          const invalidFiles = newFiles.filter(file => !isSupportedCvFile(file));

                          if (invalidFiles.length > 0) {
                            toast.error(t('cvs.errors.invalidFileType'));
                            e.target.value = '';
                            return;
                          }

                          if (validFiles.length === 0) {
                            toast.error(t('cvs.errors.noValidSelected'));
                            e.target.value = '';
                            return;
                          }

                          setSelectedFiles([...selectedFiles, ...validFiles]);
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFiles([]);
                }}
                className="btn-secondary"
                disabled={uploading}
              >
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                onClick={() => handleUpload()}
                disabled={uploading || selectedFiles.length === 0}
                className="btn-primary"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('cvs.upload.processingExtraction', 'Upload + Extraction...')}
                  </>
                ) : (
                  t('cvs.upload.startExtraction', 'Uploader + Extraire le texte')
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Candidate Modal */}
      {showCandidateModal && (
        <CandidateModal
          isOpen={showCandidateModal}
          onClose={() => {
            setShowCandidateModal(false);
            setSelectedCandidate(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries('candidatesAll');
            setShowCandidateModal(false);
            setSelectedCandidate(null);
          }}
          candidate={selectedCandidate}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal {...confirmProps} />
      
      {/* Send Appointment Modal */}
      <SendAppointmentModal
        isOpen={showAppointmentModal}
        onClose={() => {
          setShowAppointmentModal(false);
          setSelectedCandidateForAppointment(null);
        }}
        candidateData={selectedCandidateForAppointment}
      />

      {/* CV Preview Modal */}
      <CVPreview
        cv={cvPreviewData}
        isOpen={showCVPreview}
        onClose={() => {
          setShowCVPreview(false);
          setCvPreviewData(null);
        }}
      />
    </div>
  );
};

export default CVsPage;
